import { useEffect } from 'react';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://gits.agency';

function upsertJsonLd(id: string, data: object): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data, null, 0);
}

function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) el.remove();
}

const schemaIds = [
  'schema-website',
  'schema-person',
  'schema-organization',
  'schema-faqpage',
  'schema-service-consulting',
  'schema-service-project',
  'schema-service-products',
];

export default function SEOJsonLd() {
  useEffect(() => {
    // ── WebSite ──────────────────────────────────────────────────────────────
    upsertJsonLd('schema-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'GITS – Global Innovation Technology Solutions',
      url: siteUrl,
      description:
        'AI consulting, fractional Head of AI services, voice agents, and custom AI solutions by Peter Michael Gits. Based in Charlotte, NC.',
    });

    // ── Person ───────────────────────────────────────────────────────────────
    upsertJsonLd('schema-person', {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Peter Michael Gits',
      jobTitle: 'Fractional Head of AI',
      url: siteUrl,
      email: 'support@gits.agency',
      telephone: '+17433005858',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '509 Vermeer Court',
        addressLocality: 'Mount Holly',
        addressRegion: 'NC',
        postalCode: '28120',
        addressCountry: 'US',
      },
      worksFor: {
        '@type': 'Organization',
        name: 'GITS – Global Innovation Technology Solutions',
        url: siteUrl,
      },
      knowsAbout: [
        'Artificial Intelligence',
        'Conversational AI',
        'Voice AI Agents',
        'AI Strategy',
        'Enterprise Technology',
        'Machine Learning',
        'Natural Language Processing',
      ],
      sameAs: ['https://www.linkedin.com/in/peter-michael-gits/'],
      description:
        'Technology executive and AI Innovation Leader with 25+ years of experience. Former Cisco engineer with 40+ issued patents and $2B+ in revenue generated.',
    });

    // ── Organization / LocalBusiness ─────────────────────────────────────────
    upsertJsonLd('schema-organization', {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: 'GITS – Global Innovation Technology Solutions',
      alternateName: 'GITS',
      url: siteUrl,
      email: 'support@gits.agency',
      telephone: '+17433005858',
      description:
        'AI consulting, fractional Head of AI services, voice agents, and custom AI solutions. Based in Charlotte, NC.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '509 Vermeer Court',
        addressLocality: 'Mount Holly',
        addressRegion: 'NC',
        postalCode: '28120',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.3,
        longitude: -81.0,
      },
      areaServed: [
        { '@type': 'City', name: 'Charlotte' },
        { '@type': 'State', name: 'North Carolina' },
      ],
      founder: {
        '@type': 'Person',
        name: 'Peter Michael Gits',
      },
      sameAs: ['https://www.linkedin.com/in/peter-michael-gits/'],
    });

    // ── FAQPage ──────────────────────────────────────────────────────────────
    upsertJsonLd('schema-faqpage', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a Fractional Head of AI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A Fractional Head of AI is an experienced AI executive who works with your company on a part-time or project basis. You get the strategic leadership and technical expertise of a seasoned AI leader — without the cost of a full-time C-suite hire. Peter embeds with your team, understands your business, and drives your AI initiatives forward.',
          },
        },
        {
          '@type': 'Question',
          name: 'What types of businesses do you work with?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Peter works with businesses of all sizes — from startups looking to build their first AI product to mid-market companies wanting to scale AI across their operations, to enterprises seeking to modernize legacy systems with AI. Industries include healthcare, finance, retail, logistics, and professional services.',
          },
        },
        {
          '@type': 'Question',
          name: 'How quickly can we get started?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'After an initial discovery call, most engagements can begin within 1–2 weeks. Peter moves quickly to assess your current state, identify high-impact opportunities, and start delivering value from day one.',
          },
        },
        {
          '@type': 'Question',
          name: 'What does an AI consulting engagement look like?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Engagements typically start with a comprehensive AI readiness assessment, followed by strategy development, roadmap creation, and hands-on execution support. We work closely with your team throughout — from ideation to deployment — ensuring AI initiatives are practical, scalable, and aligned with business goals.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you build custom AI solutions?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes. Peter's team builds custom AI solutions including chatbots, voice agents, automation pipelines, recommendation engines, and more. Every solution is tailored to your specific use case, integrated with your existing systems, and built for long-term maintainability.",
          },
        },
        {
          '@type': 'Question',
          name: 'What is VoiceCal and how can it help my business?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "VoiceCal is an AI-powered voice booking system that allows customers to schedule appointments naturally over the phone — no apps, no forms, just conversation. It's ideal for service businesses like medical practices, salons, law firms, and any business that relies on appointment scheduling.",
          },
        },
        {
          '@type': 'Question',
          name: 'How do you measure the success of AI projects?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Success is measured against clear, pre-defined business metrics — such as cost reduction, revenue increase, time savings, customer satisfaction scores, or conversion rates. Peter establishes KPIs at the start of every engagement and provides regular reporting on progress and ROI.',
          },
        },
        {
          '@type': 'Question',
          name: 'What makes you different from other AI consultants?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "We bring a rare combination of deep technical expertise (25+ years at Cisco, 40+ patents) and executive leadership experience. We don't just advise — we roll up our sleeves and build. Our track record of delivering $2B+ in revenue impact speaks to our ability to translate AI potential into real business results.",
          },
        },
      ],
    });

    // ── Service: Consulting ──────────────────────────────────────────────────
    upsertJsonLd('schema-service-consulting', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Consulting / Fractional Head of AI',
      description:
        'Get Fortune 500-level AI leadership without the full-time executive cost. Peter embeds with your team to define AI strategy, build roadmaps, and drive execution — acting as your dedicated Head of AI on a flexible engagement model.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'GITS – Global Innovation Technology Solutions',
        url: siteUrl,
      },
      areaServed: { '@type': 'City', name: 'Charlotte' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'AI Consulting Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI Strategy & Roadmap' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Team Building & Mentorship' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vendor Evaluation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Executive Advisory' } },
        ],
      },
    });

    // ── Service: AI Project Work ─────────────────────────────────────────────
    upsertJsonLd('schema-service-project', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Custom AI Project Work',
      description:
        'From concept to deployment, Peter and his team build custom AI solutions tailored to your specific business challenges — chatbots, voice agents, automation pipelines, and more.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'GITS – Global Innovation Technology Solutions',
        url: siteUrl,
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'AI Development Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom AI Development' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Chatbots & Voice Agents' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Process Automation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI Integration' } },
        ],
      },
    });

    // ── SoftwareApplication: VoiceCal ────────────────────────────────────────
    upsertJsonLd('schema-service-products', {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'VoiceCal Booking System',
      operatingSystem: 'Web',
      applicationCategory: 'BusinessApplication',
      description:
        'An AI-powered voice booking system that lets customers schedule appointments naturally over the phone — no apps, no forms, just conversation.',
      offers: {
        '@type': 'Offer',
        url: siteUrl,
      },
      author: {
        '@type': 'Organization',
        name: 'GITS – Global Innovation Technology Solutions',
        url: siteUrl,
      },
    });

    return () => {
      schemaIds.forEach(removeJsonLd);
    };
  }, []);

  return null;
}
